import { z } from 'zod';

import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { FormFieldConfig } from '../types';

// Makes hidden paths optional within a ZodObject by extending it with z.any() overrides
// for leaf fields and recursing into nested objects. Uses z.any() instead of .optional()
// because .optional() preserves min()/refine() constraints that still reject '' or [].
function makeZodObjectFieldsOptional(schema: z.ZodObject, hiddenPaths: string[]): z.ZodType {
  const shape = schema.shape as Record<string, z.ZodType>;
  const overrides: Record<string, z.ZodType> = {};

  const groups = new Map<string, string[]>();
  for (const p of hiddenPaths) {
    const dot = p.indexOf('.');
    const head = dot === -1 ? p : p.slice(0, dot);
    const tail = dot === -1 ? '' : p.slice(dot + 1);
    if (!(head in shape)) continue;
    if (!groups.has(head)) groups.set(head, []);
    if (tail) groups.get(head)!.push(tail);
  }

  for (const [head, tails] of groups) {
    overrides[head] = tails.length ? makeFieldsOptionalInSchema(shape[head], tails) : z.any();
  }

  return Object.keys(overrides).length ? schema.safeExtend(overrides as z.ZodRawShape) : schema;
}

// Makes hidden paths optional within a ZodArray. Array-named tasks always have a single
// item at index 0 (enforced by createCustomerTaskFormConfig), so relaxing the element
// schema is safe — there are no other indices whose validation would be accidentally widened.
function makeZodArrayItemsOptional(schema: z.ZodArray, hiddenPaths: string[]): z.ZodType {
  const subPaths: string[] = [];
  for (const p of hiddenPaths) {
    const match = /^(\d+)\.(.+)$/.exec(p);
    if (!match) continue;
    const idx = parseInt(match[1], 10);
    if (idx !== 0) {
      console.error(
        `DeclarativeForm: hidden fields in an array schema must be at index 0 — found index ${idx}. Skipping visibility-derived schema.`
      );
      return schema;
    }
    subPaths.push(match[2]);
  }
  if (!subPaths.length) return schema;

  // schema.element is typed as core.$ZodType (the abstract base) because ZodArray's
  // generic defaults to that, but at runtime it's always a concrete ZodType.
  const elementSchema = schema.element as z.ZodType;
  return z.array(makeFieldsOptionalInSchema(elementSchema, subPaths));
}

// Returns a copy of `schema` where every dotted path in `hiddenPaths` is made
// optional, so zodResolver succeeds and returns properly coerced/transformed values.
// Handles ZodObject (flat and nested) and ZodArray (array-named tasks).
export function makeFieldsOptionalInSchema(schema: z.ZodType, hiddenPaths: string[]): z.ZodType {
  if (!hiddenPaths.length) return schema;
  if (schema instanceof z.ZodObject) return makeZodObjectFieldsOptional(schema, hiddenPaths);
  if (schema instanceof z.ZodArray) return makeZodArrayItemsOptional(schema, hiddenPaths);
  return schema;
}

// Deep-clones plain objects and arrays so deleteAtPath can mutate the clone without
// touching the resolver's `values` object. Non-plain-object values (File, Date, etc.)
// are copied by reference — they're leaves, never mutated by deleteAtPath.
export function deepClone<T>(value: T): T {
  if (Array.isArray(value)) return value.map(deepClone) as unknown as T;
  if (value !== null && typeof value === 'object' && value.constructor === Object) {
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, deepClone(val)])) as T;
  }
  return value;
}

// Mutably deletes a dotted-path key from obj and prunes any parent that becomes empty.
export function deleteAtPath(obj: Record<string, unknown>, fieldName: string): void {
  const parts = fieldName.split('.');
  if (parts.length === 1) {
    delete obj[fieldName];
    return;
  }
  const [head, ...rest] = parts;
  const nested = obj[head];
  if (nested != null && typeof nested === 'object') {
    deleteAtPath(nested as Record<string, unknown>, rest.join('.'));
    if (Object.keys(nested as object).length === 0) {
      delete obj[head];
    }
  }
}

export function isFieldVisible<TFieldValues extends FieldValues>(
  fieldConfig: FormFieldConfig<TFieldValues>,
  values: TFieldValues,
  form: UseFormReturn<TFieldValues> | undefined
): boolean {
  if (!fieldConfig.shouldRender) return true;
  // form is undefined in the resolver path (circular ref — form isn't assigned yet when
  // useForm's options are constructed). If the callback accesses context without a null
  // check it will throw. Catch only in that path and treat as visible: conservative means
  // we never silently suppress an error or strip a value for a field whose visibility we
  // couldn't determine. When form is defined, let errors propagate so real bugs surface.
  if (form === undefined) {
    try {
      return fieldConfig.shouldRender(values, undefined);
    } catch {
      return true;
    }
  }
  return fieldConfig.shouldRender(values, form);
}

// A name is hidden only when every config claiming it has shouldRender=false.
// If any claimant is visible the name stays visible (shared-name / relabeling pattern).
export function partitionFieldsByVisibility<TFieldValues extends FieldValues>(
  fields: FormFieldConfig<TFieldValues>[],
  values: TFieldValues,
  form: UseFormReturn<TFieldValues> | undefined
): { visibleNames: Set<string>; hiddenNames: Set<string> } {
  const visibleNames = new Set<string>();
  const hiddenNames = new Set<string>();
  fields.forEach((fieldConfig) => {
    const isVisible = isFieldVisible(fieldConfig, values, form);
    (isVisible ? visibleNames : hiddenNames).add(String(fieldConfig.name));
  });
  visibleNames.forEach((n) => hiddenNames.delete(n));
  return { visibleNames, hiddenNames };
}

// Removes hidden field values from a validated payload before it reaches onSubmit.
// Uses form.getValues() (raw input) for visibility checks — not the coerced `values`
// object — so shouldRender comparisons against string literals stay accurate even
// when a field has a z.preprocess coercion (e.g. string → number).
// Clones before mutating with deleteAtPath so the resolver's `values` object (which
// react-hook-form may still reference) is left untouched.
export function stripHiddenFieldValues<TFieldValues extends FieldValues>(
  values: TFieldValues,
  fields: FormFieldConfig<TFieldValues>[],
  form: UseFormReturn<TFieldValues>
): Record<string, unknown> {
  const { hiddenNames } = partitionFieldsByVisibility(fields, form.getValues() as TFieldValues, form);
  const cleaned = deepClone(values) as Record<string, unknown>;
  hiddenNames.forEach((name) => deleteAtPath(cleaned, name));
  return cleaned;
}
